import { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';

import { isNumeric, Issue } from '../../constants';
import config from '../../config';
import { useCache } from '../../App.hooks';
import { useIssueNotFound } from '../../hooks/use-issue';

/**
 * React hook that returns the details of the issue
 *
 * @param id - Identifier of the issue, can be a slug or an issue id
 *
 */
export function useGetIssue(id: string): GetIssueResponse {
	const [issue, setIssue] = useState<Issue | null>();
	const fetchPolicy = useCache(
		`preview:${id}:issue`,
		config?.app?.cache?.ttl?.getIssue,
		{
			id: 'ROOT_QUERY',
			fieldName: `issue({"id":"${id}","isSlug":${!isNumeric(id)}})`,
			broadcast: false,
		},
	);

	const { loading, error, data } = useQuery<{ issue: Issue | null }>(
		PREVIEW_ISSUE,
		{
			fetchPolicy,
			variables: { id, isSlug: !isNumeric(id) },
		},
	);

	useIssueNotFound(issue);

	useEffect(() => {
		if (loading) return;
		if (error) {
			console.error(error);
			return;
		}
		if (!data) return;

		const { issue } = data;
		setIssue(issue);
	}, [loading, error, data]);

	return {
		isLoading: loading,
		error: error ? new Error(error.message) : null,
		issue,
	};
}

interface GetIssueResponse {
	issue: any | null | undefined;
	isLoading: boolean;
	error?: Error | null;
}

const PREVIEW_ISSUE = gql`
	query PreviewIssue($id: ID!, $isSlug: Boolean!) {
		issue(id: $id, isSlug: $isSlug) {
			id
			name
			description
			cover
			features
			slug
			articles {
				id
				ArticleID
				role
				name
				channel
				features
				index
				slug
				description
				isFeatured
				updated
				created
			}
		}
	}
`;
